/**
 * Rainbow Wallet deep links — open this dapp in Rainbow without adding RainbowKit.
 * https://rainbow.me/dapp?url=…  (web / universal)
 * rainbow://dapp?url=…           (iOS / Android scheme)
 *
 * Edge cases handled here:
 *  - localhost / preview / sandbox origins are never handed to Rainbow (the app
 *    cannot reach them, which is what produced the "dapp failed to open" error).
 *    We fall back to the canonical public origin instead.
 *  - http origins are upgraded to the canonical https origin.
 *  - desktop browsers get the universal https link; mobile gets the scheme with
 *    a universal-link fallback if the scheme does not resolve.
 */

import { SITE_ORIGIN } from "./site";
import { normalizeWalletError } from "./walletErrors";

export const RAINBOW_WEB = "https://rainbow.me/dapp";
export const RAINBOW_APP = "https://rnbwapp.com/dapp";
export const RAINBOW_SCHEME = "rainbow://dapp";

const CANONICAL_ORIGIN = SITE_ORIGIN.replace(/\/$/, "");

const NON_PUBLIC_HOST =
  /(^localhost$|^127\.|^0\.0\.0\.0$|^\[::1\]$|\.local$|^id-preview|lovableproject\.com$|lovable\.app$|\.ngrok|\.vercel\.app$)/i;

/** True when an origin can actually be fetched by a wallet's in-app browser. */
export function isPublicDappOrigin(origin: string): boolean {
  try {
    const u = new URL(origin);
    if (u.protocol !== "https:") return false;
    return !NON_PUBLIC_HOST.test(u.hostname);
  } catch {
    return false;
  }
}

function publicOrigin(): string {
  const current =
    typeof window !== "undefined" && window.location?.origin ? window.location.origin : "";
  return current && isPublicDappOrigin(current) ? current.replace(/\/$/, "") : CANONICAL_ORIGIN;
}

/** Absolute, wallet-reachable URL for a dapp path. */
export function dappUrlForRainbow(path = "/"): string {
  let p = path && path !== "" ? path : "/";
  if (/^https?:\/\//i.test(p)) {
    try {
      const u = new URL(p);
      p = `${u.pathname}${u.search}${u.hash}`;
    } catch {
      p = "/";
    }
  }
  if (!p.startsWith("/")) p = `/${p}`;
  return `${publicOrigin()}${p}`;
}

export function isMobileWalletBrowser(): boolean {
  if (typeof navigator === "undefined") return false;
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

export function rainbowDeepLink(path = "/", kind: "web" | "app" | "scheme" = "web"): string {
  const url = encodeURIComponent(dappUrlForRainbow(path));
  if (kind === "app") return `${RAINBOW_APP}?url=${url}`;
  if (kind === "scheme") return `${RAINBOW_SCHEME}?url=${url}`;
  return `${RAINBOW_WEB}?url=${url}`;
}

/** Kind that actually resolves on the current device. */
export function preferredRainbowKind(): "web" | "scheme" {
  return isMobileWalletBrowser() ? "scheme" : "web";
}

/**
 * Open Rainbow for this path. On mobile we try the custom scheme and fall back
 * to the universal link when the app is not installed. Returns the URL used, or
 * an error string when the browser blocked the navigation.
 */
export function openRainbow(path = "/"): { ok: boolean; href: string; error?: string } {
  const universal = rainbowDeepLink(path, "web");
  if (typeof window === "undefined") return { ok: false, href: universal, error: "No browser context." };

  if (isMobileWalletBrowser()) {
    const scheme = rainbowDeepLink(path, "scheme");
    let left = false;
    const onHide = () => {
      left = true;
    };
    document.addEventListener("visibilitychange", onHide, { once: true });
    try {
      window.location.href = scheme;
    } catch {
      /* scheme unsupported — universal fallback below */
    }
    window.setTimeout(() => {
      document.removeEventListener("visibilitychange", onHide);
      if (!left && !document.hidden) window.location.href = universal;
    }, 1200);
    return { ok: true, href: scheme };
  }

  const win = window.open(universal, "_blank", "noopener,noreferrer");
  if (!win) {
    return {
      ok: false,
      href: universal,
      error: "Your browser blocked the Rainbow window. Allow pop-ups or open the link manually.",
    };
  }
  return { ok: true, href: universal };
}

export type InjectedEthereum = {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
  isRainbow?: boolean;
};

export const EIP3326_MAINNET = {
  chainId: "0x1",
  chainName: "Ethereum Mainnet",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
  rpcUrls: ["https://ethereum.publicnode.com"],
  blockExplorerUrls: ["https://etherscan.io"],
} as const;

/** EIP-3326 — switch to Ethereum mainnet; 4902 → wallet_addEthereumChain. */
export async function switchEthereumMainnet(ethereum: InjectedEthereum): Promise<"0x1"> {
  try {
    await ethereum.request({
      method: "wallet_switchEthereumChain",
      params: [{ chainId: EIP3326_MAINNET.chainId }],
    });
  } catch (err) {
    const kind = normalizeWalletError(err).kind;
    if (kind === "unsupported-chain") {
      await ethereum.request({
        method: "wallet_addEthereumChain",
        params: [EIP3326_MAINNET],
      });
    } else {
      throw err;
    }
  }
  return "0x1";
}

export async function connectInjectedWallet(
  ethereum: InjectedEthereum | undefined,
): Promise<{ address: `0x${string}`; injected: "rainbow" | "injected"; chainId: "0x1" | string } | null> {
  if (!ethereum?.request) return null;
  const accounts = (await ethereum.request({ method: "eth_requestAccounts" })) as string[];
  const address = accounts?.[0];
  if (!address || !/^0x[a-fA-F0-9]{40}$/.test(address)) return null;
  let chainId: "0x1" | string = "0x1";
  try {
    chainId = await switchEthereumMainnet(ethereum);
  } catch {
    const hex = (await ethereum.request({ method: "eth_chainId" }).catch(() => "0x0")) as string;
    chainId = hex || "0x0";
  }
  return {
    address: address as `0x${string}`,
    injected: ethereum.isRainbow ? "rainbow" : "injected",
    chainId,
  };
}
